<div class="table-container">
  @if (tableTitle()) {
    <h2>{{ tableTitle() }}</h2>
  }

  @if (showPaginator()) {
    <mat-paginator
      [pageSizeOptions]="pageSizeOptions()"
      [length]="totalRecords()"
      [pageIndex]="pageIndex()"
      [pageSize]="pageSize()"
      (page)="pageChangeEvent($event)"
      [showFirstLastButtons]="true"
      aria-label="Select page of periodic elements">
    </mat-paginator>
  }
  <table [dataSource]="tableData()" class="dyn-table table-background" mat-table>
    <ng-container matColumnDef="select">
      <th *matHeaderCellDef mat-header-cell>
        @if (allowMultiSelect()) {
          <mat-checkbox
            id="toggle-all"
            (change)="list.toggleAll()"
            [checked]="list.allSelected()"
            [indeterminate]="list.hasSelection() && !list.allSelected()">
          </mat-checkbox>
        }
      </th>
      <td *matCellDef="let row" mat-cell>
        <mat-checkbox [checked]="list.isSelected(row)"> </mat-checkbox>
      </td>
    </ng-container>

    <ng-container matColumnDef="actions">
      <th *matHeaderCellDef mat-header-cell style="text-align: center">Actions</th>
      <td *matCellDef="let row" mat-cell style="text-align: center">
        <div class="btn-row">
          @if ((allowEdit() && modificationSettings().allowEdit) || row['isNew']) {
            <button
              type="button"
              class="edit-button"
              (click)="handleEdit(row); $event.stopPropagation()"
              mat-button>
              {{ editSelection() === row ? 'Done' : 'Edit' }}
            </button>
          }
          @if (allowEdit() && modificationSettings().allowDelete) {
            <button
              type="button"
              class="delete-button"
              (click)="handleDelete(row); $event.stopPropagation()"
              mat-button>
              Delete
            </button>
          }
        </div>
      </td>
    </ng-container>

    @for (col of columns(); track col) {
      <ng-container matColumnDef="{{ col.id }}">
        <th *matHeaderCellDef mat-header-cell>{{ col.label }}</th>
        <td *matCellDef="let element" mat-cell>
          @switch (col.id) {
            @case ('go-to') {
              <ng-container
                [ngTemplateOutletContext]="{ element: element }"
                [ngTemplateOutlet]="goTo"></ng-container>
            }
            @case ('status') {
              <ng-container
                [ngTemplateOutletContext]="{ element: element }"
                [ngTemplateOutlet]="statusTemplate"></ng-container>
            }
            @default {
              @let data = element[col.id] ?? '';
              @if (editSelection() !== element || !(col.isModifiable || element['isNew'])) {
                @if (col['type'] === 'date') {
                  {{ data ? (data | formatDate) : '' }}
                } @else if (col['type'] === 'bool') {
                  <mat-checkbox [checked]="!!data" disabled></mat-checkbox>
                } @else if (col['type'] === 'option') {
                  {{ toArray(data).join(', ') }}
                } @else {
                  <div
                    (click)="handleShowFullText($event, element, col.id)"
                    (keydown.enter)="handleShowFullText($event, element, col.id)"
                    class="expandable"
                    tabindex="0">
                    @if (showFullTextSelection() === element) {
                      <div>
                        <!-- Check if ID is nested key (contains '.') -->
                        @if (col.id.includes('.')) {
                          {{ getNestedElement(col.id, element) }}
                        } @else {
                          {{ data }}
                        }
                      </div>
                    } @else {
                      <div>
                        <!-- Check if ID is nested key (contains '.') -->
                        @if (col.id.includes('.')) {
                          {{
                            typeof getNestedElement(col.id, element) === 'string'
                              ? getNestedElement(col.id, element)
                              : ('' | truncate: lengthLimit : true)
                          }}
                        } @else {
                          {{ data | truncate: lengthLimit : true }}
                        }
                      </div>
                    }
                  </div>
                }
              }
              @if (editSelection() === element && (col.isModifiable || element['isNew'])) {
                @switch (col.type) {
                  @case ('date') {
                    <mat-form-field appearance="fill">
                      <mat-label>Choose a date</mat-label>
                      <input [(ngModel)]="element[col.id]" [matDatepicker]="picker" matInput />
                      <mat-datepicker-toggle [for]="picker" matSuffix></mat-datepicker-toggle>
                      <mat-datepicker #picker></mat-datepicker>
                    </mat-form-field>
                  }
                  @case ('textArea') {
                    <mat-form-field class="edit-text-field-area">
                      <textarea
                        #autosize="cdkTextareaAutosize"
                        [(ngModel)]="element[col.id]"
                        cdkAutosizeMaxRows="20"
                        cdkAutosizeMinRows="1"
                        cdkTextareaAutosize
                        matInput></textarea>
                    </mat-form-field>
                  }
                  @case ('option') {
                    <mat-form-field appearance="fill">
                      <mat-label>{{ col.label }}</mat-label>
                      <mat-select [(ngModel)]="element[col.id]" multiple>
                        @for (opt of col.options ?? []; track opt) {
                          <mat-option [value]="opt">{{ opt }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                  @case ('bool') {
                    <mat-checkbox [(ngModel)]="element[col.id]"></mat-checkbox>
                  }
                  @default {
                    <mat-form-field>
                      <input [(ngModel)]="element[col.id]" [type]="col.type" matInput />
                    </mat-form-field>
                  }
                }
              }
            }
          }
        </td>
      </ng-container>
    }

    <tr *matHeaderRowDef="displayedColumns()" mat-header-row></tr>
    <tr
      (click)="handleRowClick($event, i)"
      (keydown.enter)="handleRowClick($event, i)"
      *matRowDef="let row; columns: displayedColumns(); let i = index"
      [routerLink]="row.routerLink"
      class="data-row"
      mat-row></tr>
  </table>
  @if (allowEdit() && modificationSettings().allowNew) {
    <div class="further-actions-row">
      <button cuiButton variant="plain" type="button" (click)="addItem()">
        <mat-icon>playlist_add</mat-icon>
        Add Item
      </button>
    </div>
  }
</div>

<ng-template #statusTemplate let-element="element">
  <mat-icon
    [matTooltip]="element.status"
    [style.color]="element.status | apply: loadIconColorFromStatus">
    {{ element.status | apply: loadIconNameFromStatus }}
  </mat-icon>
</ng-template>

<ng-template #goTo let-element="element">
  @if (element.routerLink) {
    <button
      type="button"
      mat-icon-button
      matTooltip="Go to result"
      routerLink="{{ element.routerLink }}">
      <mat-icon>exit_to_app</mat-icon>
    </button>
  }
</ng-template>
